import React, { useCallback, useMemo, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { enqueueAndSync, hasPending, queueStats, retryFailed } from '../../lib/portalQueue';
import { Button, StatusPill, MobileScreen, MobileHeader, MobileCard, EmptyState } from '../ui';
import {
    formatDateShort, timeToMin, fileToBase64, billableOf, dayUsesTruck,
    usePortalQueue, usePhotoCapture, PhotoCaptureField, notifyOutcome, OfflineBanner,
} from './portalShared';

// ---------------------------------------------------------------------------
// End of day tab — the checklist that was the floating bar (recaps done /
// hours saved / truck photo / sync state), the daily truck reset photo,
// and Sign out at the bottom.
// ---------------------------------------------------------------------------

// The reset-truck photo is a DAILY element (one per day, end-of-shift).
// Uploads via save_day_truck_photo, which attaches the photo to the day's
// last billable led job's recap_photo_url so the office sees it where it
// always has.
function DailyTruckPhotoCard({ billable, validDate, token, onSaved }) {
    const { show } = useToast();
    const queue = usePortalQueue();
    const [saving, setSaving] = useState(false);
    const onPhotoError = useCallback((msg) => show(msg, 'error'), [show]);
    const capture = usePhotoCapture(onPhotoError);
    const photoQueued = hasPending(`truckphoto:${validDate}`, queue);

    // Existing photo: the latest-starting billable job that already has one.
    const existingUrl = useMemo(() => {
        const withPhoto = billable.filter(j => j.recap_photo_url);
        if (withPhoto.length === 0) return null;
        return [...withPhoto].sort((a, b) => timeToMin(b.actual_start_time || b.time) - timeToMin(a.actual_start_time || a.time))[0].recap_photo_url;
    }, [billable]);

    const save = async () => {
        if (!capture.file) { show('Snap or pick a photo first', 'error'); return; }
        setSaving(true);
        try {
            const b64 = await fileToBase64(capture.file);
            const { outcome, error } = await enqueueAndSync({
                key: `truckphoto:${validDate}`,
                action: 'save_day_truck_photo',
                label: 'Truck reset photo',
                payload: {
                    action: 'save_day_truck_photo',
                    token: token || undefined,
                    workDate: validDate,
                    reset_photo_base64: b64,
                    reset_photo_filename: capture.file.name || 'reset.jpg',
                },
            });
            notifyOutcome(show, outcome, error, 'Truck reset photo saved');
            if (outcome === 'synced') { capture.clear(); onSaved?.(); }
        } catch (e) {
            show(e?.message || 'Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <MobileCard
            title="Truck reset photo"
            right={existingUrl
                ? <StatusPill label="On file" color="#059669" />
                : <StatusPill label="Needed" color="#d97706" />}
        >
            {photoQueued && <OfflineBanner what="truck photo" className="mb-2" />}
            <p className="text-[11px] text-slate-500 leading-snug mb-2">
                One photo of the truck after end-of-day reset (company policy) — covers the whole day, not each job.
            </p>
            <PhotoCaptureField
                capture={capture}
                existingUrl={existingUrl}
                emptyLabel="📷 Add reset truck photo"
                emptyHint="Snap the back of the truck after reset."
                previewAlt="Reset truck"
                accent="amber"
            />
            <Button variant="secondary" full className="mt-3" onClick={save} loading={saving || capture.processing} disabled={!capture.file}>
                Save truck photo
            </Button>
        </MobileCard>
    );
}

function CheckRow({ done, label, detail }) {
    return (
        <div className="flex items-center gap-2.5 py-2">
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md text-[11px] font-bold flex-shrink-0 ${done ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-400'}`} aria-hidden="true">
                {done ? '✓' : ''}
            </span>
            <span className={`text-[13px] flex-1 ${done ? 'text-slate-800 font-semibold' : 'text-slate-500'}`}>{label}</span>
            {detail && <span className="text-[12px] text-slate-400 tabular-nums">{detail}</span>}
        </div>
    );
}

export default function EndOfDayTab({ isLead, ledJobs, crewDayHours, validDate, token, onSaved, onSignOut, tabs, strip }) {
    const queue = usePortalQueue();
    const header = <MobileHeader title="End of day" sub={formatDateShort(validDate)} />;

    const billable = billableOf(ledJobs);
    const recapped = (j) => !!j.recap_submitted_at || hasPending(`recap:${j.id}`, queue);
    const recapDone = billable.filter(recapped).length;
    const hoursDone = (Array.isArray(crewDayHours) && crewDayHours.length > 0) || hasPending(`dayhours:${validDate}`, queue);
    // No truck used today (pack-only / labor-only) → the photo isn't owed
    // and can't block "You're done for the day" (Jaylen 2026-08-07).
    const truckApplies = dayUsesTruck(billable);
    const photoDone = !truckApplies || billable.some(j => j.recap_photo_url) || hasPending(`truckphoto:${validDate}`, queue);
    const stats = queueStats(queue);
    const sync = stats.failed > 0 ? 'attention' : stats.pending > 0 ? 'pending' : 'synced';
    const allComplete = recapDone === billable.length && hoursDone && photoDone;
    const syncPill = sync === 'attention'
        ? { label: 'Needs attention', color: '#dc2626' }
        : sync === 'pending'
            ? { label: `${stats.pending} pending`, color: '#d97706' }
            : { label: 'All synced', color: '#059669' };

    const signOut = (
        <Button variant="tertiary" size="lg" full onClick={onSignOut}>Sign out</Button>
    );

    if (!isLead || billable.length === 0) {
        return (
            <MobileScreen header={header} strip={strip} tabs={tabs}>
                <EmptyState
                    title={isLead ? 'Nothing to wrap up' : 'Lead only'}
                    hint={isLead ? 'No billable jobs on your board today.' : 'Your crew lead closes out the day — recaps, hours and the truck photo.'}
                />
                {signOut}
            </MobileScreen>
        );
    }

    return (
        <MobileScreen header={header} strip={strip} tabs={tabs}>
            <MobileCard
                title={allComplete ? "You're done for the day" : 'Finish up'}
                right={<StatusPill label={syncPill.label} color={syncPill.color} />}
                className={allComplete ? 'border-emerald-300' : ''}
            >
                <div className="divide-y divide-slate-100">
                    <CheckRow done={recapDone === billable.length} label="Job recaps" detail={`${recapDone} of ${billable.length}`} />
                    <CheckRow done={hoursDone} label="Crew hours saved" />
                    {truckApplies && <CheckRow done={photoDone} label="Truck reset photo" />}
                </div>
                {sync === 'pending' && (
                    <p className="text-[11px] text-amber-700 leading-snug mt-2">
                        {stats.pending} change{stats.pending !== 1 ? 's' : ''} saved on this phone, waiting for signal. Keep the app open until they sync.
                    </p>
                )}
                {sync === 'attention' && (
                    <div className="flex items-center justify-between gap-2 mt-2">
                        <p className="text-[11px] text-red-600 leading-snug">
                            {stats.failed} item{stats.failed !== 1 ? 's' : ''} couldn't sync (likely needs a re-sign-in).
                        </p>
                        <Button variant="secondary" size="sm" onClick={() => retryFailed()}>Retry</Button>
                    </div>
                )}
            </MobileCard>

            {truckApplies && <DailyTruckPhotoCard billable={billable} validDate={validDate} token={token} onSaved={onSaved} />}

            <div className="pt-2">{signOut}</div>
            <p className="text-center text-[11px] text-slate-400">Beaty Brothers Moving — crew portal</p>
        </MobileScreen>
    );
}
